import type { FilterValueType } from '@repo/common/components/Filter/types';
import {
	FilterDropdownType,
	FilterOperator,
} from '@repo/common/components/Filter/types';
import { makeAutoObservable } from 'mobx';
import type { FilterValue } from '../../../Filter';
import { FilterOptionType } from '../../../Filter';
import { FILTER_OPTIONS_CONFIG } from '../../../Filter/constants';
import { LineageDirectionEnum } from '../../types';

type FILTER_OPTION_TYPES =
	| FilterOptionType.NATIVE_TYPE
	| FilterOptionType.INTEGRATION
	| FilterOptionType.DIRECTION
	| FilterOptionType.DEPTH;
const FILTERS_TO_PRELOAD = [
	FilterOptionType.NATIVE_TYPE,
	FilterOptionType.INTEGRATION,
];

type LineageListParams = {
	direction: LineageDirectionEnum;
	depth: number;
	include_columns: boolean;
	filter?: string;
};

class ImpactAnalysisStore {
	id: string | undefined = undefined;

	opened = false;

	filterValues: Record<FILTER_OPTION_TYPES, FilterValue> = {
		[FilterOptionType.NATIVE_TYPE]: {
			filterType: FilterOptionType.NATIVE_TYPE,
			value: [],
			operator: FilterOperator.Is,
		},
		[FilterOptionType.INTEGRATION]: {
			filterType: FilterOptionType.INTEGRATION,
			value: [],
			operator: FilterOperator.Is,
		},
		[FilterOptionType.DIRECTION]: {
			filterType: FilterOptionType.DIRECTION,
			value: LineageDirectionEnum.DOWNSTREAM,
			operator: FilterOperator.Is,
		},
		[FilterOptionType.DEPTH]: {
			filterType: FilterOptionType.DEPTH,
			value: 3,
			operator: FilterOperator.Is,
		},
	};

	searchTerm = '';

	showSearch = false;

	constructor() {
		makeAutoObservable(this);
	}

	get queryParams() {
		const nativeTypes = (this.filterValues[FilterOptionType.NATIVE_TYPE]
			.value ?? []) as FilterValueType[];
		const integrationIds = (this.filterValues[FilterOptionType.INTEGRATION]
			.value ?? []) as FilterValueType[];

		const params: LineageListParams = {
			direction: this.filterValues[FilterOptionType.DIRECTION]
				.value as LineageDirectionEnum,
			depth: this.filterValues[FilterOptionType.DEPTH].value as number,
			include_columns: true,
		};

		// Build filter operands only if there are filters to apply
		const filterOperands: any[] = [];
		const notFilterOperands: any[] = [];

		if (nativeTypes.length > 0) {
			const operands = nativeTypes.map((type) => ({
				operator: 'exact',
				field: 'native_type',
				value: type,
				operands: [],
			}));

			if (
				this.filterValues[FilterOptionType.NATIVE_TYPE].operator ===
				FilterOperator.Is
			) {
				filterOperands.push(...operands);
			} else {
				notFilterOperands.push(...operands);
			}
		}

		if (integrationIds.length > 0) {
			const operands = integrationIds.map((id) => ({
				operator: 'exact',
				field: 'integration_id',
				value: id,
				operands: [],
			}));

			if (
				this.filterValues[FilterOptionType.INTEGRATION].operator ===
				FilterOperator.Is
			) {
				filterOperands.push(...operands);
			} else {
				notFilterOperands.push(...operands);
			}
		}

		// Only add filter parameter if we have operands to apply
		if (filterOperands.length > 0 || notFilterOperands.length > 0) {
			const finalOperands: any[] = [];

			if (filterOperands.length > 0) {
				finalOperands.push({
					operator: 'or',
					operands: filterOperands,
				});
			}

			if (notFilterOperands.length > 0) {
				finalOperands.push({
					operator: 'not',
					operands: [
						{
							operator: 'or',
							operands: notFilterOperands,
						},
					],
				});
			}

			params.filter = JSON.stringify({
				operator: 'and',
				operands: finalOperands,
			});
		}

		return params;
	}

	async initialize() {
		FILTERS_TO_PRELOAD.forEach((option) => {
			const filterConfig = FILTER_OPTIONS_CONFIG[option];

			if (
				filterConfig.filterDropdownConfig.dropdownType !==
				FilterDropdownType.List
			) {
				return;
			}

			const { getItems } = filterConfig.filterDropdownConfig;

			if (typeof getItems !== 'function') {
				return;
			}

			Promise.resolve(getItems());
		});
	}

	setId = (id: string) => {
		this.id = id;
	};

	setOpened = (opened: boolean) => {
		this.opened = opened;
	};

	setValue = (
		filterOption: FILTER_OPTION_TYPES,
		value: Partial<FilterValue>
	) => {
		this.filterValues[filterOption] = {
			...this.filterValues[filterOption],
			...value,
		};
	};

	setFilterValues = (values: FilterValue[]) => {
		// Reset all filter values to their defaults
		this.filterValues = {
			[FilterOptionType.NATIVE_TYPE]: {
				filterType: FilterOptionType.NATIVE_TYPE,
				value: [],
				operator: FilterOperator.Is,
			},
			[FilterOptionType.INTEGRATION]: {
				filterType: FilterOptionType.INTEGRATION,
				value: [],
				operator: FilterOperator.Is,
			},
			[FilterOptionType.DIRECTION]: {
				filterType: FilterOptionType.DIRECTION,
				value: LineageDirectionEnum.DOWNSTREAM,
				operator: FilterOperator.Is,
			},
			[FilterOptionType.DEPTH]: {
				filterType: FilterOptionType.DEPTH,
				value: 3,
				operator: FilterOperator.Is,
			},
		};

		// Update with new values
		values.forEach((value) => {
			if (value.filterType in this.filterValues) {
				this.filterValues[value.filterType as FILTER_OPTION_TYPES] = value;
			}
		});
	};

	setSearchTerm = (searchTerm: string) => {
		this.searchTerm = searchTerm;
	};

	setShowSearch = (showSearch: boolean) => {
		this.showSearch = showSearch;
	};
}

export const impactAnalysisStore = new ImpactAnalysisStore();
